import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Copy, Share2, DollarSign, Users, TrendingUp, Clock,
  ArrowUpRight, ArrowDownRight, MousePointer, Wallet,
  ChevronRight, RefreshCw, ExternalLink, CheckCircle,
  AlertCircle, CreditCard
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const PartnerDashboardPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);
  
  // Bank info modal state
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankIban, setBankIban] = useState('');
  const [bankName, setBankName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profileRes, statsRes, detailedRes] = await Promise.all([
        axios.get('/partner/profile'),
        axios.get('/partner/stats'),
        axios.get('/partner/detailed-stats')
      ]);
      
      setProfile(profileRes.data);
      setStats(statsRes.data);
      setDetailedStats(detailedRes.data);
      
      // Bank bilgilerini set et
      if (profileRes.data.bank_iban) {
        setBankIban(profileRes.data.bank_iban);
        setBankName(profileRes.data.bank_name || '');
      }
    } catch (error) {
      console.error('Error fetching partner data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/register?ref=${profile?.referral_code || ''}`;
    navigator.clipboard.writeText(link);
    toast.success('Referans linki kopyalandı!');
  };

  const requestPayout = async () => {
    if (!profile?.bank_iban) {
      setShowBankModal(true);
      toast.error('Önce banka bilgilerinizi girin');
      return;
    }
    
    try {
      setPayoutLoading(true);
      const res = await axios.post('/partner/request-payout', null, {
        params: { bank_iban: profile.bank_iban, bank_name: profile.bank_name }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme talebi başarısız');
    } finally {
      setPayoutLoading(false);
    }
  };

  const saveBankInfo = async () => {
    try {
      await axios.put('/partner/bank-info', null, {
        params: { bank_iban: bankIban, bank_name: bankName }
      });
      toast.success('Banka bilgileri kaydedildi');
      setShowBankModal(false);
      fetchData();
    } catch (error) {
      toast.error('Kaydetme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-slate-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const referralLink = `${window.location.origin}/register?ref=${profile?.referral_code || ''}`;
  const earningsGrowth = detailedStats?.earnings?.last_month > 0 
    ? ((detailedStats?.earnings?.this_month - detailedStats?.earnings?.last_month) / detailedStats.earnings.last_month * 100)
    : 0;

  return (
    <div className="min-h-screen bg-brand-dark py-8">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Partner Dashboard</h1>
            <p className="text-slate-400">Referanslarını takip et, kazancını yönet</p>
          </div>
          <div className="flex gap-3">
            <Link to="/partner">
              <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800">
                <ExternalLink className="h-4 w-4 mr-2" /> Partner Sayfası
              </Button>
            </Link>
            <Button 
              onClick={requestPayout}
              disabled={payoutLoading || (stats?.pending_earnings || 0) < 100}
              className="bg-primary hover:bg-primary/90 text-brand-dark"
            >
              <Wallet className="h-4 w-4 mr-2" /> Ödeme Talep Et
            </Button>
          </div>
        </div>

        {/* Referral Link Card */}
        <Card className="mb-8 border-2 border-primary/30 bg-slate-900/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-white">Senin Referans Linkin</span>
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    Kod: {profile?.referral_code}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    value={referralLink}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                  />
                  <Button onClick={copyLink} className="bg-primary hover:bg-primary/90 text-brand-dark px-6">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-slate-800 bg-slate-900/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <MousePointer className="h-5 w-5 text-blue-400" />
                  <Badge variant="outline" className="text-xs border-blue-400/30 text-blue-400">
                    {detailedStats?.clicks?.this_month || 0} bu ay
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-white">{detailedStats?.clicks?.total || 0}</div>
                <div className="text-sm text-slate-400">Toplam Tıklama</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-slate-800 bg-slate-900/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Users className="h-5 w-5 text-green-400" />
                  <Badge variant="outline" className="text-xs border-green-400/30 text-green-400">
                    {detailedStats?.referrals?.active || 0} aktif
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</div>
                <div className="text-sm text-slate-400">Toplam Referans</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-slate-800 bg-slate-900/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="h-5 w-5 text-yellow-400" />
                  {earningsGrowth !== 0 && (
                    <Badge variant="outline" className={`text-xs ${earningsGrowth > 0 ? 'border-green-400/30 text-green-400' : 'border-red-400/30 text-red-400'}`}>
                      {earningsGrowth > 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                      {Math.abs(earningsGrowth).toFixed(0)}%
                    </Badge>
                  )}
                </div>
                <div className="text-2xl font-bold text-white">₺{(detailedStats?.earnings?.this_month || 0).toLocaleString('tr-TR')}</div>
                <div className="text-sm text-slate-400">Bu Ay Kazanç</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-slate-800 bg-slate-900/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                    %{stats?.commission_rate || 20}
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-white">₺{(detailedStats?.earnings?.total || 0).toLocaleString('tr-TR')}</div>
                <div className="text-sm text-slate-400">Toplam Kazanç</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Earnings Overview */}
          <Card className="lg:col-span-2 border-slate-800 bg-slate-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                Kazanç Özeti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 rounded-xl bg-slate-800/50">
                  <div className="text-sm text-slate-400 mb-1">Bekleyen</div>
                  <div className="text-2xl font-bold text-yellow-400">₺{(detailedStats?.earnings?.pending || 0).toLocaleString('tr-TR')}</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-slate-800/50">
                  <div className="text-sm text-slate-400 mb-1">Ödenen</div>
                  <div className="text-2xl font-bold text-green-400">₺{(detailedStats?.earnings?.paid || 0).toLocaleString('tr-TR')}</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-slate-800/50">
                  <div className="text-sm text-slate-400 mb-1">Geçen Ay</div>
                  <div className="text-2xl font-bold text-slate-300">₺{(detailedStats?.earnings?.last_month || 0).toLocaleString('tr-TR')}</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 rounded-xl bg-primary/10 border border-primary/20">
                <div>
                  <div className="text-sm text-slate-400">Minimum Ödeme Eşiği</div>
                  <div className="text-lg font-bold text-white">₺{stats?.min_payout || 100}</div>
                </div>
                <Button 
                  onClick={() => setShowBankModal(true)}
                  variant="outline" 
                  className="border-primary/30 text-primary hover:bg-primary/10"
                >
                  <CreditCard className="h-4 w-4 mr-2" /> Banka Bilgileri
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Rates */}
          <Card className="border-slate-800 bg-slate-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Dönüşüm Oranları
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-800/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Tıklama → Kayıt</span>
                  <span className="text-lg font-bold text-blue-400">{detailedStats?.conversion?.click_to_register || 0}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-400 rounded-full transition-all"
                    style={{ width: `${Math.min(detailedStats?.conversion?.click_to_register || 0, 100)}%` }}
                  />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-slate-800/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Kayıt → Ödeme</span>
                  <span className="text-lg font-bold text-green-400">{detailedStats?.conversion?.register_to_paid || 0}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-400 rounded-full transition-all"
                    style={{ width: `${Math.min(detailedStats?.conversion?.register_to_paid || 0, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals */}
          <Card className="lg:col-span-2 border-slate-800 bg-slate-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-400" />
                Son Referanslar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detailedStats?.recent_referrals?.length > 0 ? (
                <div className="space-y-3">
                  {detailedStats.recent_referrals.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ref.status === 'subscribed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {ref.status === 'subscribed' ? <CheckCircle className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">Referans #{ref.id?.substring(0, 8)}</div>
                          <div className="text-xs text-slate-400">{new Date(ref.created_at).toLocaleDateString('tr-TR')}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={ref.status === 'subscribed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {ref.status === 'subscribed' ? 'Aktif' : 'Kayıtlı'}
                        </Badge>
                        {ref.total_commission > 0 && (
                          <div className="text-sm text-primary mt-1">+₺{ref.total_commission.toLocaleString('tr-TR')}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Henüz referans yok</p>
                  <p className="text-sm text-slate-500">Linkini paylaşarak kazanmaya başla!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="border-slate-800 bg-slate-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-400" />
                Son İşlemler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {detailedStats?.recent_transactions?.length > 0 ? (
                <div className="space-y-3">
                  {detailedStats.recent_transactions.map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                      <div>
                        <div className="text-sm font-medium text-white">Komisyon</div>
                        <div className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString('tr-TR')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary">+₺{tx.commission_amount?.toLocaleString('tr-TR')}</div>
                        <Badge variant="outline" className={`text-xs ${tx.status === 'paid' ? 'border-green-400/30 text-green-400' : 'border-yellow-400/30 text-yellow-400'}`}>
                          {tx.status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Henüz işlem yok</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bank Info Modal */}
        {showBankModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold text-white mb-4">Banka Bilgileri</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Banka Adı</label>
                  <Input 
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Örn: Ziraat Bankası"
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">IBAN</label>
                  <Input 
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBankModal(false)}
                  className="flex-1 border-slate-700 text-white hover:bg-slate-800"
                >
                  İptal
                </Button>
                <Button 
                  onClick={saveBankInfo}
                  className="flex-1 bg-primary hover:bg-primary/90 text-brand-dark"
                >
                  Kaydet
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboardPage;
